import { Navigate, useParams } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";

import { SourceConfiguration } from "./source/configuration";
import { DemoSourceConfiguration } from "./source/demo-configuration";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { useSourceQuery } from "src/graphql";
import { lazyNamed } from "src/utils/lazy-named";

const sourcesImport = () => import("./sources");

const SourceLinking = lazyNamed(
  () => import("./source/linking"),
  "SourceLinking",
);
const SourceGrants = lazyNamed(() => import("./source/grants"), "SourceGrants");
const Sources = lazyNamed(sourcesImport, "Sources");

export const preloads: RoutePreload[] = [["/sources", sourcesImport]];
const SourceSyncLog = lazyNamed(
  () => import("./source/sync-log"),
  "SourceSyncLog",
);
const SourceElement = lazyNamed(() => import("./source"), "Element");
const CreateSource = lazyNamed(() => import("./create-source"), "CreateSource");
const SourceRoleMapping = lazyNamed(
  () => import("./source/role-mapping"),
  "SourceRoleMapping",
);

const ConfigurationRouter = () => {
  const { id } = useParams<{ id: string }>();
  const { data: source } = useSourceQuery(
    { id: String(id) },
    { select: (data) => data.connections_by_pk },
  );

  if (source!.definition.isSampleDataSource) {
    return <DemoSourceConfiguration />;
  }
  return <SourceConfiguration />;
};

export const routes: Array<RouteObject> = [
  {
    path: "sources",
    spacesAccess: "hidden-from-limited-view",
    element: <Sources />,
  },
  {
    path: "sources/new",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={{ v2: { resource: "source", grant: "can_create" } }}
        redirect="/sources"
      >
        <CreateSource />
      </PermissionedRoute>
    ),
  },
  {
    path: "sources/:id/*",
    spacesAccess: "hidden-from-limited-view",
    element: <SourceElement />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="configuration" replace />,
      },
      {
        path: "configuration",
        spacesAccess: "hidden-from-limited-view",
        element: <ConfigurationRouter />,
      },
      {
        path: "linking",
        spacesAccess: "hidden-from-limited-view",
        element: <SourceLinking />,
      },
      {
        path: "permissions",
        spacesAccess: "hidden-from-limited-view",
        element: <SourceGrants />,
      },
      {
        path: "sync-logs",
        spacesAccess: "hidden-from-limited-view",
        element: <SourceSyncLog />,
      },
      {
        path: "role-mapping",
        spacesAccess: "hidden-from-limited-view",
        element: <SourceRoleMapping />,
      },
    ],
  },
];
